import i18n from "../i18n";

export const buildEmployeeLookups = (employees = []) => {
  const byId = new Map();
  const byRegistration = new Map();
  const byBadge = new Map();

  employees.forEach((employee) => {
    if (!employee) return;

    if (employee.id !== undefined && employee.id !== null) {
      byId.set(Number(employee.id), employee);
    }

    if (employee.registration_number) {
      byRegistration.set(String(employee.registration_number), employee);
    }

    if (employee.badge_number) {
      byBadge.set(String(employee.badge_number), employee);
    }
  });

  return { byId, byRegistration, byBadge };
};

const findEmployeeMatch = (record, lookups) => {
  if (!record || !lookups) {
    return null;
  }

  const { byId, byRegistration, byBadge } = lookups;

  const idCandidates = [record.employee_id, record.employeeId];
  for (const candidate of idCandidates) {
    if (candidate !== undefined && candidate !== null) {
      const match = byId.get(Number(candidate));
      if (match) return match;
    }
  }

  const registrationCandidates = [
    record.registration_number,
    record.registrationNumber,
    record.employee_registration,
    record.user_registration,
    record.user_id,
  ];
  for (const candidate of registrationCandidates) {
    if (!candidate && candidate !== 0) continue;
    const normalized = String(candidate);
    const match = byRegistration.get(normalized);
    if (match) return match;
  }

  const badgeCandidates = [
    record.employee_badge,
    record.badge_number,
    record.badge,
    record.user_badge,
    record.user_id,
  ];

  for (const candidate of badgeCandidates) {
    if (!candidate && candidate !== 0) continue;
    const normalized = String(candidate);
    const match = byBadge.get(normalized);
    if (match) return match;
  }

  return null;
};

const buildDisplayName = (record, employee) => {
  const firstName = (record?.first_name || employee?.first_name || "").trim();
  const lastName = (record?.last_name || employee?.last_name || "").trim();
  const combined = [firstName, lastName].filter(Boolean).join(" ").trim();
  if (combined) return combined;
  if (employee?.full_name) return employee.full_name;
  if (record?.employee_name) return record.employee_name;
  if (record?.registration_number || employee?.registration_number) {
    return i18n.t("common.registrationWithNumber", { number: record?.registration_number || employee?.registration_number });
  }
  if (record?.employee_badge || employee?.badge_number) {
    return i18n.t("common.badgeWithNumber", { number: record?.employee_badge || employee?.badge_number });
  }
  if (record?.user_id) {
    return `ID ${record.user_id}`;
  }
  return i18n.t("common.unknownEmployee");
};

export const enrichRecordsWithEmployeeData = (records = [], lookups = { byId: new Map(), byRegistration: new Map(), byBadge: new Map() }) => {
  if (!Array.isArray(records)) return [];

  return records.map((record) => {
    const matchedEmployee = findEmployeeMatch(record, lookups);

    if (!matchedEmployee) {
      return {
        ...record,
        employee_display_name: buildDisplayName(record),
      };
    }

    const firstName = record.first_name || matchedEmployee.first_name;
    const lastName = record.last_name || matchedEmployee.last_name;
    const registrationNumber = record.registration_number || matchedEmployee.registration_number;
    const badgeNumber = record.employee_badge || matchedEmployee.badge_number;
    const departmentName = record.department_name || matchedEmployee.department_name;
    const departmentId = record.department_id || matchedEmployee.department_id;
    const employeeId = record.employee_id || matchedEmployee.id;

    return {
      ...record,
      first_name: firstName,
      last_name: lastName,
      registration_number: registrationNumber,
      employee_badge: badgeNumber,
      department_name: departmentName,
      department_id: departmentId,
      employee_id: employeeId,
      employee_display_name: buildDisplayName(
        {
          ...record,
          first_name: firstName,
          last_name: lastName,
          registration_number: registrationNumber,
          employee_badge: badgeNumber,
        },
        matchedEmployee
      ),
    };
  });
};
